import { useRef, type RefObject } from 'react';
import {
  Button,
  ExpandIcon,
  PencilIcon,
  ToolkitIcon,
  Tooltip,
  TrashIcon,
  TOOLTIP_SURFACE,
} from '@repo/atomic-components';
import { ToolkitTool } from '@/api';
import { renderClampedText } from '@/utils';
import { FlipCard } from './FlipCard';

interface MyPromptToolItemProps {
  tool: ToolkitTool;
  handleToolClick: (tool: ToolkitTool) => void;
  onEdit: (tool: ToolkitTool) => void;
  onRequestGlobalToolkit: (tool: ToolkitTool) => void;
  onDelete: (tool: ToolkitTool) => void;
  onExpand: (tool: ToolkitTool) => void;
}

interface ActionButtonsProps {
  tool: ToolkitTool;
  onEdit: (tool: ToolkitTool) => void;
  onRequestGlobalToolkit: (tool: ToolkitTool) => void;
  onDelete: (tool: ToolkitTool) => void;
}

const ActionButtons = ({ tool, onEdit, onRequestGlobalToolkit, onDelete }: ActionButtonsProps) => {
  const editPromptTooltipRef = useRef<HTMLSpanElement>(null);
  const sharedToolkitTooltipRef = useRef<HTMLSpanElement>(null);
  const deletePromptTooltipRef = useRef<HTMLSpanElement>(null);

  return (
    <div className="flex items-center gap-1">
      <span ref={editPromptTooltipRef} className="inline-flex shrink-0">
        <Button
          type="button"
          outlined
          className="text-theme-black border-theme-border-grey hover:text-theme-dark-green hover:bg-theme-background-grey flex h-8 w-8 shrink-0 items-center justify-center"
          aria-label="Edit prompt"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(tool);
          }}
        >
          <PencilIcon className="h-[16px] w-[16px]" />
        </Button>
        <Tooltip
          target={editPromptTooltipRef as RefObject<HTMLElement>}
          content="Edit prompt"
          position="top"
          className={TOOLTIP_SURFACE}
        />
      </span>
      <span ref={sharedToolkitTooltipRef} className="inline-flex shrink-0">
        <Button
          type="button"
          outlined
          className="text-theme-black border-theme-border-grey hover:text-theme-dark-green hover:bg-theme-background-grey flex h-8 w-8 shrink-0 items-center justify-center"
          aria-label="Add prompt to shared toolkit"
          onClick={(e) => {
            e.stopPropagation();
            onRequestGlobalToolkit(tool);
          }}
        >
          <ToolkitIcon className="h-[16px] w-[16px]" />
        </Button>
        <Tooltip
          target={sharedToolkitTooltipRef as RefObject<HTMLElement>}
          content="Add prompt to shared toolkit"
          position="top"
          className={TOOLTIP_SURFACE}
        />
      </span>
      <span ref={deletePromptTooltipRef} className="inline-flex shrink-0">
        <Button
          type="button"
          outlined
          className="text-theme-black border-theme-border-grey hover:text-theme-dark-green hover:bg-theme-background-grey flex h-8 w-8 shrink-0 items-center justify-center"
          aria-label="Delete prompt"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(tool);
          }}
        >
          <TrashIcon className="h-[16px] w-[16px]" />
        </Button>
        <Tooltip
          target={deletePromptTooltipRef as RefObject<HTMLElement>}
          content="Delete prompt"
          position="top"
          className={TOOLTIP_SURFACE}
        />
      </span>
    </div>
  );
};

export const MyPromptToolItem = ({
  tool,
  handleToolClick,
  onEdit,
  onRequestGlobalToolkit,
  onDelete,
  onExpand,
}: MyPromptToolItemProps) => {
  const frontExpandRef = useRef<HTMLButtonElement>(null);
  const backExpandRef = useRef<HTMLButtonElement>(null);

  return (
    <FlipCard
      className="group"
      front={
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg">
          <button
            type="button"
            onClick={() => handleToolClick(tool)}
            className="flex min-h-0 flex-1 cursor-pointer flex-col items-start gap-2 overflow-hidden px-6 pt-6 pb-12 text-left transition-colors"
          >
            <div className="text-theme-black text-base font-semibold">{renderClampedText(tool.toolName, 2)}</div>
            <p className="text-theme-text-grey text-sm" title={tool.description}>
              {renderClampedText(tool.description, 4)}
            </p>
          </button>
          <button
            ref={frontExpandRef}
            type="button"
            aria-label="Expand prompt"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(tool);
            }}
            className="text-theme-text-grey hover:text-theme-dark-green focus:text-theme-dark-green absolute top-3 right-3 flex h-6 w-6 cursor-pointer items-center justify-center rounded border-none bg-transparent opacity-0 transition-opacity focus:opacity-100"
          >
            <ExpandIcon className="h-[14px] w-[14px]" />
          </button>
          <Tooltip
            target={frontExpandRef as RefObject<HTMLElement>}
            content="Expand prompt"
            position="top"
            className={TOOLTIP_SURFACE}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-end px-4 pb-4 opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
            <ActionButtons
              tool={tool}
              onEdit={onEdit}
              onRequestGlobalToolkit={onRequestGlobalToolkit}
              onDelete={onDelete}
            />
          </div>
        </div>
      }
      back={
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg px-6 pt-6 pb-12">
          <button
            type="button"
            tabIndex={-1}
            onClick={() => handleToolClick(tool)}
            className="flex min-h-0 flex-1 cursor-pointer flex-col text-left"
          >
            <div className="text-theme-black mb-2 text-xs font-semibold tracking-wide uppercase">Prompt</div>
            <div className="text-theme-text-grey flex-1 overflow-y-auto text-sm whitespace-pre-wrap">{tool.prompt}</div>
          </button>
          <button
            ref={backExpandRef}
            type="button"
            tabIndex={-1}
            aria-label="Expand prompt"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(tool);
            }}
            className="text-theme-text-grey hover:text-theme-dark-green absolute top-3 right-3 flex h-6 w-6 cursor-pointer items-center justify-center rounded border-none bg-transparent"
          >
            <ExpandIcon className="h-[14px] w-[14px]" />
          </button>
          <Tooltip
            target={backExpandRef as RefObject<HTMLElement>}
            content="Expand prompt"
            position="top"
            className={TOOLTIP_SURFACE}
          />
          <div className="absolute inset-x-0 bottom-0 flex justify-end px-4 pb-4">
            <ActionButtons
              tool={tool}
              onEdit={onEdit}
              onRequestGlobalToolkit={onRequestGlobalToolkit}
              onDelete={onDelete}
            />
          </div>
        </div>
      }
    />
  );
};
